/** Mirrors smart_back/server/src/account-chart/account-chart-bank.helpers.ts */

export const findAccountByChartKey = (nodes, chartKey) => {
  if (!nodes?.length || !chartKey) return null;
  for (const node of nodes) {
    if (node.key === chartKey) return node;
    const found = findAccountByChartKey(node.children, chartKey);
    if (found) return found;
  }
  return null;
};

export const findAccountNameByKey = (accounts, accountKey) =>
  findAccountByChartKey(accounts, accountKey)?.name || '';

export const resolveAccountDisplayName = (accountKey, fallbackName, accounts) => {
  if (!accountKey) return fallbackName || '';
  return findAccountNameByKey(accounts, accountKey) || fallbackName || '';
};

export const DIRECT_BANK_CASH_PARENT_KEY = '1.02.01';

export const DEFAULT_BANK_ACCOUNT_KEY = '1.02.01.01';

export const DIRECT_BANK_CASH_ACCOUNT_KEY_PATTERN = /^1\.02\.01\.[^.]+$/;

const normalizePasswordId = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  return s || null;
};

export const isDirectBankCashAccountKey = (key) =>
  DIRECT_BANK_CASH_ACCOUNT_KEY_PATTERN.test(String(key ?? '').trim());

export const getAccountPasswordIdsFromNode = (account) => {
  const fromArray = Array.isArray(account?.passwordIds) ? account.passwordIds : [];
  const ids = fromArray.map((id) => String(id).trim()).filter(Boolean);
  const legacy =
    account?.passwordId != null && String(account.passwordId).trim()
      ? String(account.passwordId).trim()
      : null;
  if (legacy && !ids.includes(legacy)) {
    ids.unshift(legacy);
  }
  return [...new Set(ids)];
};

/**
 * passwordId → account key (1.02.01.xx). Build once per plan load and reuse.
 */
export const buildPasswordIdToAccountKeyMap = (accounts) => {
  const map = new Map();
  const walk = (nodes) => {
    if (!nodes?.length) return;
    for (const node of nodes) {
      const accountKey = String(node?.key ?? '').trim();
      if (accountKey && isDirectBankCashAccountKey(accountKey)) {
        for (const pid of getAccountPasswordIdsFromNode(node)) {
          map.set(pid, accountKey);
        }
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(accounts);
  return map;
};

export const resolveBankAccountKey = (
  transaction,
  accounts,
  fallbackKey = DEFAULT_BANK_ACCOUNT_KEY,
  passwordIdToAccountKeyMap = null,
) => {
  const pid = normalizePasswordId(transaction?.passwordId);
  if (!pid) return fallbackKey;
  const map = passwordIdToAccountKeyMap ?? buildPasswordIdToAccountKeyMap(accounts);
  return map.get(pid) ?? fallbackKey;
};

/**
 * Same rules as backend assertPasswordIdsOnlyOnDirectBankCashAccounts +
 * assertEachPasswordIdAssignedAtMostOnce, with pending edits applied.
 *
 * @param {Array} accounts - account chart root nodes
 * @param {Record<string, string[]>} editedPasswordIdsByKey - pending passwordIds by account.key
 * @returns {string|null} error message or null if valid
 */
export const validateBankPasswordAssignments = (accounts, editedPasswordIdsByKey = {}) => {
  const ownerByPasswordId = new Map();

  const getEffectiveIds = (accountKey, account) => {
    if (Object.prototype.hasOwnProperty.call(editedPasswordIdsByKey, accountKey)) {
      const edited = editedPasswordIdsByKey[accountKey];
      return Array.isArray(edited) ? edited.map((id) => String(id).trim()).filter(Boolean) : [];
    }
    return getAccountPasswordIdsFromNode(account);
  };

  const walk = (nodes) => {
    if (!nodes?.length) return null;
    for (const node of nodes) {
      const accountKey = String(node?.key ?? '').trim();
      const ids = getEffectiveIds(accountKey, node);

      if (ids.length > 0 && accountKey && !isDirectBankCashAccountKey(accountKey)) {
        return `passwordIds solo puede asignarse a cuentas hijas directas de 1.02.01 (recibido: ${accountKey}).`;
      }

      if (isDirectBankCashAccountKey(accountKey)) {
        for (const pid of ids) {
          const existing = ownerByPasswordId.get(pid);
          if (existing && existing !== accountKey) {
            return `La conexión bancaria ya está asociada a la cuenta ${existing}. Cada conexión solo puede vincularse a una cuenta contable.`;
          }
          ownerByPasswordId.set(pid, accountKey);
        }
      }

      if (node.children?.length) {
        const childError = walk(node.children);
        if (childError) return childError;
      }
    }
    return null;
  };

  return walk(accounts);
};
